// src/services/paths/robotPath/RobotPathClient.ts
// One place that talks to pathfinder's /path/simulate: turn a Mission into the
// request the robot's drive model understands, and hand back what it answered.
// The request mirrors mission_ops.to_ros_msg so the simulated mission is the
// mission the robot will actually be sent.

import axios from 'axios';
import { flatten, orderBy } from 'lodash';
import type { MultiPolygon, Polygon, Position } from 'geojson';
import { Mission, ZoneType } from '../../../db';
import { cleanSet } from '../../../utils';
import { extractPathfinderErrorDetail, pathfinderHeaders, pathfinderSimulateUrl } from '../pathfinderApi';
import { implementsCenteredNavigation, pathfinderMissionType } from '../pathfinderMission';
import type { NavigationPointType } from '../types';
import type { RobotPathRequest, RobotPathResult } from './types';

/**
 * A zone boundary row as stored on a `Boundary`: missioncontrol keeps [lat, lon]
 * positions there (`Zone.toMultiPolygon()` is what flips them into GeoJSON
 * [lon, lat], and `to_ros_msg` packs x = lon / y = lat).
 */
type ZoneBoundaryRing = { coordinates: Position[] };

/** /path/simulate takes [lon, lat] pairs (pathfinder's parse_rings). */
const toLonLatRing = (boundary: ZoneBoundaryRing): [number, number][] =>
  boundary.coordinates.map(([lat, lon]) => [lon, lat]);

/** The clearance the drive model keeps from the raw field: the robot's own unsafe_dist. */
export const ROBOT_UNSAFE_DISTANCE_M = 5;

type RoutingRings = {
  outerBoundaries: [number, number][][];
  innerBoundaries: [number, number][][];
  unsafeDistanceMeters: number;
};

/**
 * The raw mission rings as to_ros_msg sends them: FIELD outer rings outside,
 * FIELD holes plus every UNSAFE outer ring as holes. Fallback only — see
 * `routingRings`.
 */
function rawMissionRings(mission: Mission): {
  outerBoundaries: [number, number][][];
  innerBoundaries: [number, number][][];
} {
  const zones = mission.getZones();
  const fieldZones = zones.filter((zone) => zone.zone_type === ZoneType.FIELD);
  const unsafeZones = zones.filter((zone) => zone.zone_type === ZoneType.UNSAFE);

  return {
    outerBoundaries: cleanSet(flatten(fieldZones.map((zone) => zone.getOuterZoneBoundarys()))).map(toLonLatRing),
    innerBoundaries: [
      ...cleanSet(flatten(fieldZones.map((zone) => zone.getInnerZoneBoundarys()))),
      ...cleanSet(flatten(unsafeZones.map((zone) => zone.getOuterZoneBoundarys()))),
    ].map(toLonLatRing),
  };
}

/** Split a GeoJSON Polygon/MultiPolygon into its outer rings and its holes ([lon, lat]). */
function geoJsonRings(geometry: Polygon | MultiPolygon): {
  outerBoundaries: [number, number][][];
  innerBoundaries: [number, number][][];
} {
  const polygons = geometry.type === 'Polygon' ? [geometry.coordinates] : geometry.coordinates;
  const outerBoundaries: [number, number][][] = [];
  const innerBoundaries: [number, number][][] = [];

  for (const polygon of polygons) {
    const [outer, ...holes] = polygon;
    outerBoundaries.push(outer.map(([lon, lat]) => [lon, lat] as [number, number]));
    innerBoundaries.push(...holes.map((hole) => hole.map(([lon, lat]) => [lon, lat] as [number, number])));
  }

  return { outerBoundaries, innerBoundaries };
}

/**
 * What the drive model judges the mission against, and with which margin.
 *
 * `minDistanceToBoundaryMeters` is the mission's clearance and the single source
 * of truth: it is applied ONCE as geometry (this safe-routing polygon — field
 * inset, unsafe zones expanded and subtracted, bridges glued), and the verdict
 * is margin 0 against it. So `inBounds: false` means "the driven path leaves the
 * clearance the mapmaker chose", not "within 5 m of the raw edge".
 *
 * If that polygon cannot be built, fall back to the raw mission rings with the
 * robot's own 5 m margin (the robot's verdict) rather than silently weakening
 * the check to "inside the field".
 */
function routingRings(mission: Mission): RoutingRings {
  const safeRoutingBoundary = mission.getSafeRoutingBoundary();

  if (safeRoutingBoundary) {
    return { ...geoJsonRings(safeRoutingBoundary.geometry), unsafeDistanceMeters: 0 };
  }

  return { ...rawMissionRings(mission), unsafeDistanceMeters: ROBOT_UNSAFE_DISTANCE_M };
}

/**
 * Build the /path/simulate body for a mission.
 *
 * Boundaries are the safe-routing polygon with `unsafeDistanceMeters: 0` (see
 * `routingRings`) — deliberately not the raw rings `to_ros_msg` sends the robot,
 * and deliberately without SLOW rings: the robot treats a slow zone as keep-out,
 * but routing must not detour around one (a slow zone only tells the operator to
 * slow down / drive manually), so the routing polygon leaves them out.
 */
export function buildRobotPathRequest(mission: Mission, turnRadiusMeters: number): RobotPathRequest {
  const { outerBoundaries, innerBoundaries, unsafeDistanceMeters } = routingRings(mission);

  const missionWaypoints = cleanSet(orderBy(mission.getWaypoints(), ['waypoint_number'], ['asc']));

  const waypoints = missionWaypoints.map((waypoint, index) => {
    const core = waypoint.getCorePoint()?.getSoilCore();
    const sampleId = core?.getSample()?.sample_id;
    let type: NavigationPointType;

    if (core) {
      // A core is a visit; its sample ties same-sample cores together for the
      // robot's core-to-core rule.
      type = 'nav-visit';
    } else if (index === 0) {
      type = 'nav-starting-position';
    } else if (index === missionWaypoints.length - 1) {
      type = 'nav-end-position';
    } else {
      type = 'nav-grid-point';
    }

    const bearingAzimuthDeg = waypoint.bearing_azimuth_deg;

    return {
      lat: waypoint.lat,
      lon: waypoint.lon,
      type,
      ...(sampleId ? { sampleId } : {}),
      ...(typeof bearingAzimuthDeg === 'number' && Number.isFinite(bearingAzimuthDeg) ? { bearingAzimuthDeg } : {}),
    };
  });

  return {
    waypoints,
    outerBoundaries,
    innerBoundaries,
    unsafeDistanceMeters,
    turnRadiusMeters,
    // Carbon-probe jobs export cores with command 3, which the robot's is_c2c
    // (mapper.h) excludes from the core-to-core path type. Pathfinder mirrors
    // that gate with this flag, so send the same predicate as to_ros_msg.
    usesCarbonProbe: mission.usesCarbonProbe(),
    implementCenteredNavigation: implementsCenteredNavigation(mission),
    missionType: pathfinderMissionType(mission),
  };
}

/**
 * Drive a mission on the robot's own model and report what it does: the driven
 * hops, the loops the mission would make, and the total length.
 *
 * Rejects with an Error carrying pathfinder's own detail (the same message
 * /path/find surfaces) so callers can show it verbatim.
 */
export async function simulateRobotPath(mission: Mission, turnRadiusMeters: number): Promise<RobotPathResult> {
  const request = buildRobotPathRequest(mission, turnRadiusMeters);
  const requestStartedAt = Date.now();

  try {
    const response = await axios.post<RobotPathResult>(pathfinderSimulateUrl(), request, {
      headers: pathfinderHeaders(),
    });

    return response.data;
  } catch (error) {
    const elapsedSec = Math.round((Date.now() - requestStartedAt) / 1000);

    throw new Error(extractPathfinderErrorDetail(error, elapsedSec));
  }
}
